import { ESdChangeReason, EWFAssignmentType, IProjectRole, IWFNodeAssignment, IWorkFlow } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import { RoleModal } from "../../properties-panel/authorization-panel/role-modal";
import { DynamicAssignmentActionSelection } from "./dynamic-assignment-action-selection";
import "./workflow-assignment-properties.scss";

interface IAssignmentTypeOption {
  id: string;
  name: string;
}

export interface IWorkflowAssignmentPropertiesState {
  roleModalVisible: boolean;
  currentRoleId: string | undefined;
}

export interface IWorkflowAssignmentPropertiesProps {
  wf: IWorkFlow;
  nodeAssignment: IWFNodeAssignment;
  disabled: boolean;
  onNodeAssignmentDataChanged: (nodeAssignment: IWFNodeAssignment) => void;
  /** Hosts that provide their own "Assignment" section header (the step panel) hide this one. */
  hideLabel?: boolean;
  /**
   * User-task hosts pass these to render the "Use Lane" toggle directly under "Type".
   * Gating on the callback keeps Use Lane user-task-only: the lane panel omits both
   * props, so the row never renders there.
   */
  useLaneAssignment?: boolean;
  onUseLaneChange?: (val: boolean) => void;
}

class WorkflowAssignmentProperties extends PureSdComponent<
  IWorkflowAssignmentPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowAssignmentPropertiesState
> {
  constructor(props: IWorkflowAssignmentPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      roleModalVisible: false,
      currentRoleId: ""
    };
  }

  componentDidMount() {
    this.setState({ currentRoleId: this.props.nodeAssignment.roleAssignmentParameter?.id });
  }

  getRoles = (): IProjectRole[] => {
    let roles: IProjectRole[] = _.cloneDeep(this.props.sd.project.roles);
    if (roles === undefined) {
      roles = [];
    }
    // Guarantee the currently-assigned role has an option, so the role selectbox
    // shows its NAME instead of the raw GUID. The select renders the raw `value`
    // when no option matches it; a role assigned via the DSL — especially one just
    // created with create_role in the same batch, before its project.roles
    // update propagated to this panel's props — would otherwise display its id
    // (KOA-17362: the "İK" lane showed the role GUID and the role "didn't
    // appear"). The stored name lives on roleAssignmentParameter.name.
    const assigned: IProjectRole | undefined = this.props.nodeAssignment?.roleAssignmentParameter;
    if (assigned?.id && !roles.some((role: IProjectRole) => role.id === assigned.id)) {
      roles = [
        ...roles,
        { id: assigned.id, name: assigned.name ?? assigned.id, startingScreenId: assigned.startingScreenId }
      ];
    }
    return roles;
  };

  handleAssignmentTypeChange = (val) => {
    const nodeAssignment = _.cloneDeep(this.props.nodeAssignment);
    switch (val) {
      case "None":
        nodeAssignment.assignmentType = EWFAssignmentType.none;
        nodeAssignment.assignmentId = "None";
        break;
      case "Everyone":
        nodeAssignment.assignmentType = EWFAssignmentType.everyone;
        nodeAssignment.assignmentId = "Everyone";
        break;
      case "Dynamic":
        nodeAssignment.assignmentType = EWFAssignmentType.dynamic;
        nodeAssignment.assignmentId = "Dynamic";
        break;
      case "Role":
        nodeAssignment.assignmentType = EWFAssignmentType.role;
        nodeAssignment.assignmentId = "Role";
        break;
      case "External":
        nodeAssignment.assignmentType = EWFAssignmentType.external;
        nodeAssignment.assignmentId = "External";
        break;
      default:
        nodeAssignment.assignmentType = EWFAssignmentType.everyone;
        nodeAssignment.assignmentId = "Everyone";
        break;
    }
    nodeAssignment.dynamicAssignmentAction = undefined;
    nodeAssignment.roleAssignmentParameter = undefined;
    this.props.sd.changeReason = ESdChangeReason.WFStepMappingChange;
    this.props.onNodeAssignmentDataChanged(nodeAssignment);
  };

  closeRoleModal = () => {
    this.setState({ roleModalVisible: false });
  };

  // Awaited, so the New Role dialog keeps its spinner until the save is back.
  handleRoleAdd = async (data: IProjectRole): Promise<void> => {
    await this.props.sd.updateRole(data);
    if (data !== undefined && data.id !== "") {
      this.handleRoleAssignmentChange(data.id);
    } else {
      this.handleRoleAssignmentChange(this.state.currentRoleId);
    }
  };

  handleRoleAssignmentChange = (val: string | undefined) => {
    if (!val || val === "") {
      this.setState({ roleModalVisible: true });
    } else {
      const nodeAssignment = _.cloneDeep(this.props.nodeAssignment);
      const role = this.getRoles().find((r) => r.id === val);
      nodeAssignment.roleAssignmentParameter = role;
      nodeAssignment.assignmentType = EWFAssignmentType.role;
      nodeAssignment.assignmentId = "Role";

      this.setState({ currentRoleId: role?.id });
      this.props.onNodeAssignmentDataChanged(nodeAssignment);
    }
  };

  handleDynamicActionChange = (val) => {
    const nodeAssignment = _.cloneDeep(this.props.nodeAssignment);
    nodeAssignment.dynamicAssignmentAction = val;
    this.props.onNodeAssignmentDataChanged(nodeAssignment);
  };

  handleDynamicActionParameterChange = (param, symbol) => {
    const nodeAssignment = _.cloneDeep(this.props.nodeAssignment);
    nodeAssignment.dynamicAssignmentAction?.parameters.forEach((p) => {
      if (p.name === param.name) {
        p.symbol = symbol;
      }
    });
    this.props.onNodeAssignmentDataChanged(nodeAssignment);
  };

  render() {
    const roles = this.getRoles();
    const onUseLaneChange: ((val: boolean) => void) | undefined = this.props.onUseLaneChange;
    const useLaneAssignment: boolean = this.props.useLaneAssignment ?? false;
    const assignments: IAssignmentTypeOption[] = [
      // { id: "None", name: "None" },
      { id: "Everyone", name: "Everyone" },
      { id: "Role", name: "Role" },
      { id: "Dynamic", name: "Dynamic" },
      { id: "External", name: "External Users" }
    ];
    return (
      <div className="kuika-wf-assignment">
        {!this.props.hideLabel && <span className="kuika_workflow-properties__property-type">Assignment</span>}
        <div className="kuika-wf-assignment__row">
          <span className="kuika-wf-assignment__label">Type</span>
          <KuikaSelectbox
            searchable
            data-testid="wfAssignmentTypeSelect"
            disabled={this.props.disabled}
            size="sm"
            className="kuika-wf-assignment__control"
            placeholder="Assignment"
            value={this.props.sd.getAssignmentTypeValue(this.props.nodeAssignment?.assignmentType)}
            onChange={(val: string) => this.handleAssignmentTypeChange(val)}
            options={assignments.map((at: IAssignmentTypeOption): KuikaSelectboxOption => ({
              key: at.id,
              value: at.id,
              // The id rides on an element inside the label: rc-select does not carry an option's
              // own data-* onto the row it renders. searchLabel keeps the box searchable.
              label: <span data-testid={`wfAssignmentTypeOption-${at.id}`}>{at.name}</span>,
              searchLabel: at.name
            }))}
          />
        </div>

        {onUseLaneChange !== undefined && (
          <div className="kuika-wf-assignment__row">
            <span className="kuika-wf-assignment__label">Use Lane</span>
            <span className="kuika-wf-assignment__control kuika-wf-assignment__control--toggle">
              <KuikaTabs
                dataTestId="wfAssignmentUseLaneTabs"
                ariaLabel="Use Lane"
                options={[
                  {
                    value: "false",
                    icon: <KuikaSvg name="x" width={12} height={12} />,
                    dataTestId: "wfAssignmentUseLaneOff"
                  },
                  {
                    value: "true",
                    icon: <KuikaSvg name="check" width={12} height={12} />,
                    dataTestId: "wfAssignmentUseLaneOn"
                  }
                ]}
                onChange={(val: string) => onUseLaneChange(val === "true")}
                value={useLaneAssignment ? "true" : "false"}
              />
            </span>
          </div>
        )}

        <span
          style={{
            display:
              this.props.nodeAssignment.assignmentType === EWFAssignmentType.dynamic ||
              this.props.nodeAssignment.assignmentType === EWFAssignmentType.role
                ? "block"
                : "none"
          }}
        >
          <div
            style={{ display: this.props.nodeAssignment.assignmentType === EWFAssignmentType.role ? "block" : "none" }}
          >
            <div className="kuika-wf-assignment__row kuika-wf-assignment__row--stacked">
              <span className="kuika-wf-assignment__label">
                Choose {this.props.sd.getAssignmentTypeValue(this.props.nodeAssignment.assignmentType)}
              </span>
              <KuikaSelectbox
                searchable
                disabled={this.props.disabled}
                size="sm"
                className="kuika-wf-assignment__control"
                placeholder="Field"
                value={this.props.nodeAssignment.roleAssignmentParameter?.id}
                onChange={(val: string) => this.handleRoleAssignmentChange(val)}
                dropdownHeader="+ Add New Role"
                onDropdownHeaderClick={() => this.setState({ roleModalVisible: true })}
                options={roles.map((role: IProjectRole): KuikaSelectboxOption => ({
                  key: role.name,
                  value: role.id,
                  label: role.name
                }))}
              />
            </div>

            <RoleModal
              showModal={this.state.roleModalVisible}
              closeModal={this.closeRoleModal}
              onChange={this.handleRoleAdd}
              name={""}
              id={""}
              startingScreenId={""}
            ></RoleModal>
          </div>
          <div
            style={{
              display:
                this.props.nodeAssignment.assignmentType === EWFAssignmentType.dynamic && this.props.disabled === false
                  ? "block"
                  : "none"
            }}
          >
            <DynamicAssignmentActionSelection
              action={this.props.nodeAssignment.dynamicAssignmentAction}
              actionSelectedHandler={(action) => this.handleDynamicActionChange(action)}
              parameterSetHandler={(parameter, symbol) => this.handleDynamicActionParameterChange(parameter, symbol)}
            />
          </div>
        </span>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.SaveWorkflow || changeReason === ESdChangeReason.ClickWfDesignXmlNode) {
      return true;
    }
    return true;
  };
}

const workflowAssignmentProperties = withContext<IWorkflowAssignmentPropertiesProps>(
  withSD<IWorkflowAssignmentPropertiesProps>(WorkflowAssignmentProperties)
);
export { workflowAssignmentProperties as WorkflowAssignmentProperties };
